import React, { useState, createContext, useEffect } from "react";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import axios from "axios";

// ------------------- API ROUTES -------------------
const API_ROUTES = {
  LOGIN: process.env.REACT_APP_API_URL + "/auth/login",
  LOGOUT: process.env.REACT_APP_API_URL + "/auth/logout",
};

// ------------------- ENCRYPTION HELPERS -------------------
async function generateKey() {
  return await window.crypto.subtle.generateKey(
    { name: "AES-CBC", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
}

async function exportKey(key) {
  const exported = await window.crypto.subtle.exportKey("raw", key);
  return new Uint8Array(exported);
}

async function encrypt(text, key) {
  const iv = window.crypto.getRandomValues(new Uint8Array(16));
  const encoder = new TextEncoder();
  const data = encoder.encode(text);

  const encrypted = await window.crypto.subtle.encrypt(
    { name: "AES-CBC", iv },
    key,
    data
  );

  return { iv, encrypted: new Uint8Array(encrypted) };
}

// ------------------- AUTH UTILS -------------------
const authUtils = {
  login: async ({ email, password }) => {
    const key = await generateKey();
    const exportedKey = await exportKey(key);
    const { iv, encrypted } = await encrypt(password, key);

    try {
      const response = await axios.post(API_ROUTES.LOGIN, {
        email,
        password: Array.from(encrypted),
        exportedKeyArray: Array.from(exportedKey),
        ivArray: Array.from(iv),
        isUser: true,
      });

      return response.data;
    } catch (err) {
      return (
        err.response?.data || { isLoggedIn: false, message: "Login failed" }
      );
    }
  },

  logout: async () => {
    const token = Cookies.get("token");
    if (!token) return { status: "no_token" };

    let email = "";
    try {
      const decoded = jwtDecode(token);
      email = decoded?.user?.company_email ?? decoded?.user?.email ?? "";
    } catch (e) {
      console.error("Invalid token decode", e);
    }

    try {
      const response = await axios.post(
        API_ROUTES.LOGOUT,
        { email },
        { headers: { token } }
      );
      Cookies.remove("token");
      return response.data;
    } catch (err) {
      console.error("Logout error:", err);
      return { status: "error" };
    }
  },

  decodeToken: (token) => {
    try {
      const decodedVal = jwtDecode(token);
      let user = null;

      if (decodedVal.user) {
        user = decodedVal.user;
      }

      else if (decodedVal.email || decodedVal.userId) {
        user = decodedVal;
      }

      if (user) {
        user.user_id =
          user.user_id || user.id || user.userId || user._id || null;

        if (!user.id && user.user_id) {
          user.id = user.user_id;
        }
      }

      return user;
    } catch {
      return null;
    }
  },

  isTokenExpired: (token) => {
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp < currentTime;
    } catch {
      return true;
    }
  },

  getCookie: (name) => Cookies.get(name),
};

// ------------------- SESSION PROVIDER -------------------
export const SessionContext = createContext();

export const SessionProvider = ({ children }) => {
  const [session, setSession] = useState({ token: null, user: null });
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    if (session.token && session.user) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  }, [session]);

  useEffect(() => {
    const initializeSession = () => {
      const token = authUtils.getCookie("token");
      const isPendingSignup = sessionStorage.getItem("pendingSignupFlow") === "true";
      
      if (isPendingSignup) {
        setSession({ token: null, user: null });
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      if (token && !authUtils.isTokenExpired(token)) {
        const user = authUtils.decodeToken(token);
        if (user) {
          setSession({ token, user });
          setIsAuthenticated(true);

        } else {
          Cookies.remove("token");
          setSession({ token: null, user: null });
          setIsAuthenticated(false);
        }
      } else {
        Cookies.remove("token");
        setSession({ token: null, user: null });
        setIsAuthenticated(false);
      }

      setIsLoading(false);
    };

    initializeSession();
  }, []);

  const login = async ({ email, password }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email, password }),
        }
      );
      const data = await response.json();


      if (response.ok && data.isLoggedIn === true && data.token) {
        Cookies.set("token", data.token, { expires: 2 });

        let user = data.user || authUtils.decodeToken(data.token);

        if (user) {
          user.user_id =
            user.user_id || user.id || user.userId || user._id || null;

          if (!user.id && user.user_id) {
            user.id = user.user_id;
          }
        }

        if (!data.orgRequired) {
          setSession({ token: data.token, user });
          setIsAuthenticated(true);


        } else {
        
        }

        return {
          success: true,
          data: data,
        };
      }
      else {
        setIsAuthenticated(false);
        const errorMessage =
          data.message ||
          data.error ||
          data.errorMessage ||
          (!response.ok ? "Login failed" : "Invalid email or password");

        return {
          success: false,
          message: errorMessage,
          data: data,
        };
      }
    } catch (error) {
      console.error("Login error:", error);
      setIsAuthenticated(false);
      return {
        success: false,
        message: "Server error. Please try again later.",
        error: error.message,
      };
    }
  };

  const socialLogin = async (token, userData) => {
    try {
      Cookies.set("token", token, { expires: 2 });

      let user = userData;
      if (user) {
        user.user_id =
          user.user_id || user.id || user.userId || user._id || null;

        if (!user.id && user.user_id) {
          user.id = user.user_id;
        }
      }


      const isPendingSignup = sessionStorage.getItem("pendingSignupFlow") === "true";
      
      if (!isPendingSignup) {
        setSession({ token, user });
        setIsAuthenticated(true);
      } else {
      }

      return { success: true };
    } catch (error) {
      console.error("Social login error:", error);
      setIsAuthenticated(false);
      return { success: false, message: "Social login failed" };
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      await authUtils.logout();

      Cookies.remove("token");
      setSession({ token: null, user: null });
      setIsAuthenticated(false);
      sessionStorage.removeItem("pendingSignupFlow");
      return { success: true };
    } catch (error) {
      console.error("Logout error:", error);
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  const checkAuth = () => {
    const token = authUtils.getCookie("token");
    if (!token || authUtils.isTokenExpired(token)) {
      logout();
      return false;
    }
    return true;
  };

  const getUserId = () => {
    return session?.user?.user_id || session?.user?.id || null;
  };

  const value = {
    session,
    setSession,
    login,
    socialLogin,
    logout,
    isLoading,
    isAuthenticated,
    checkAuth,
    getUserId,
  };

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};